import { createReducer } from '@reduxjs/toolkit'

import {
  InternalDashboardActions as Actions,
  InternalFolderActions,
  InternalMembersActions,
  InternalProjectActions,
  LegacyMemberSet,
} from './actions'
import { StatusActions } from './statuses'
import type { Workspace } from './types'
import { findFolder, findProjectParents, updateById } from './utils'
import { findTargetSpace } from './utils/findTargetSpace'

export const dashboardReducer = createReducer<Workspace[]>([], (builder) => {
  builder
    .addCase(StatusActions.received, (state, { payload }) => {
      const parents = findProjectParents(payload.projectId, state)
      if (!parents || parents.workspace.id !== payload.workspaceId) {
        return
      }
      parents.workspace.statuses = payload.statuses
      // Shared personal projects belong to another owner's library.
      for (const folder of parents.workspace.folders) {
        for (const project of folder.projects) {
          if (project.id === payload.projectId) {
            project.status = payload.status
          } else if (project.status && project.sharedBy === null) {
            project.status =
              payload.statuses.find((s) => s.id === project.status?.id) ?? null
          }
        }
      }
    })
    .addCase(Actions.delete, (state, { payload: { workspaceId } }) =>
      state.filter((ws) => ws.id !== workspaceId)
    )
    .addCase(Actions.setWorkspaces, (_, { payload }) => payload)
    .addCase(Actions.update, (state, { payload: { workspaceId, data } }) => {
      const index = state.findIndex((ws) => ws.id === workspaceId)
      const target = state[index]
      if (!target) {
        return
      }

      state[index] = { ...target, ...data }
    })
    .addCase(
      InternalProjectActions.add,
      (state, { payload: { project, folderId, workspaceId } }) => {
        const target = findFolder(folderId, workspaceId, state)

        if (!target) {
          return state
        }

        target.projects.push(project)
      }
    )
    .addCase(InternalProjectActions.delete, (state, { payload: { id } }) => {
      const path = findProjectParents(id, state)
      if (!path) {
        return state
      }

      path.folder.projects = path.folder.projects.filter((p) => p.id !== id)
    })
    .addCase(
      InternalFolderActions.delete,
      (state, { payload: { id, workspaceId } }) => {
        const target = findTargetSpace(workspaceId, state)
        if (!target) {
          return state
        }

        target.folders = target.folders.filter((f) => f.id !== id)
      }
    )
    .addCase(
      InternalProjectActions.update,
      (state, { payload: { id, data } }) => {
        const path = findProjectParents(id, state)
        if (!path) {
          return state
        }

        const index = path.folder.projects.findIndex((p) => p.id === id)
        path.folder.projects[index] = { ...path.project, ...data }
      }
    )
    .addCase(
      InternalFolderActions.set,
      (state, { payload: { folders, workspaceId } }) => {
        const target = findTargetSpace(workspaceId, state)
        if (!target) {
          return state
        }

        target.folders = folders
      }
    )
    .addCase(InternalFolderActions.update, (state, { payload }) => {
      const { workspaceId, data, id } = payload
      const target = findTargetSpace(workspaceId, state)
      if (!target) {
        return state
      }

      target.folders = updateById(id, data, target.folders)
    })
    .addCase(
      InternalFolderActions.add,
      (state, { payload: { workspaceId, folder } }) => {
        const target = findTargetSpace(workspaceId, state)
        if (!target) {
          return state
        }

        target.folders = [...target.folders, folder].sort(
          (a, b) => a.order - b.order
        )
      }
    )
    .addCase(
      InternalProjectActions.massArchive,
      (state, { payload: { projects, workspaceId } }) => {
        const target = findTargetSpace(workspaceId, state)
        if (!target) {
          return state
        }

        const archiveIdx = target.folders.findIndex((f) => f.id === 'archive')

        target.folders[archiveIdx]?.projects.push(...projects)
      }
    )
    .addCase(LegacyMemberSet, (state, { payload: { workspaceId, data } }) => {
      const target = findTargetSpace(workspaceId, state)
      if (!target) {
        return state
      }

      target.members = data
    })
    .addCase(InternalMembersActions.update, (state, { payload }) => {
      const { id, workspaceId, ...data } = payload
      const workspace = state.find((ws) => ws.id === workspaceId)
      const index = workspace?.members?.findIndex((m) => m.id === payload.id)
      if (index === undefined || index < 0 || !workspace) {
        return
      }

      workspace.members[index] = { ...workspace.members[index], ...data }
    })
    .addCase(InternalMembersActions.remove, (state, { payload }) => {
      const { id, workspaceId } = payload
      const workspace = state.find((ws) => ws.id === workspaceId)
      if (!workspace) {
        return
      }

      workspace.members = workspace.members.filter((m) => m.id !== id)
    })
    .addCase(InternalMembersActions.set, (state, { payload }) => {
      const { workspaceId, members } = payload
      const workspace = state.find((ws) => ws.id === workspaceId)
      if (!workspace) {
        return
      }

      workspace.members = members
    })
    .addCase(InternalMembersActions.push, (state, { payload }) => {
      const { workspaceId, item } = payload
      const workspace = state.find((ws) => ws.id === workspaceId)
      if (!workspace) {
        return
      }

      workspace.members.push(item)
    })
})
